import qs from "query-string";

import eventsSchematic from "../../events/schematic";
import eventsTopographic from "../../events/topographic";
import eventsTrains from "../../events/trains";
import isEventTrain from "../../utils/isEventTrain";
import common, { getStyleUrl } from "../common";

import lineIcons from "./lineIcons";
import mapLineIcons from "./mapLineIcons";
import standingVehicleStyleFunc from "./standingVehicleStyleFunc";

import type { Extent } from "ol/extent";

import type { Config } from "../common";

const {
  VITE_SCHEMATIC_EXTENT,
  VITE_STYLE_SCHEMATIC,
  VITE_TOPOGRAPHIC_EXTENT,
}: Record<string, string> = import.meta.env;

const schematicExtent: Extent = (VITE_SCHEMATIC_EXTENT || "0,0,5469102,3370699") // 1152072,6048052,1433666,6205578"
  .split(",")
  .map((coord) => parseFloat(coord));

const topographicExtent: Extent = (
  VITE_TOPOGRAPHIC_EXTENT || "1250562,6106868,1320960,6145316"
)
  .split(",")
  .map((coord) => parseFloat(coord));

const params = qs.parse(window.location.search);

const styleSchematic = VITE_STYLE_SCHEMATIC || "tralis_munich_schematic_v3";

// SBAHN Munchen
const config: Config = {
  ...common,
  defaultLineIcon: lineIcons.s,
  disruptionInfo: false,
  getCustomMapIconForTrajectory: (trajectory, lineImgKey, lineImages) => {
    // A trajectory can display only one event
    const event = eventsTrains.find((conf) => {
      return isEventTrain(trajectory, conf);
    });
    if (event) {
      const key = event.key;
      return {
        arrow: lineImages[lineImgKey]?.arrow,
        circle: lineImages[key]?.circle,
        deltaScale: params.scale ? parseFloat(params.scale as string) : 1.5,
        key: `${key}${lineImgKey}`,
      };
    }
    return null;
  },
  getIconScaleFromRes: (view, layer) => {
    const dfltScale = 0.6;
    if (layer.mode === "topographic") {
      return dfltScale;
    }
    const minIconScale = 0.45;
    const res = view.getResolution() ?? 0;
    const resZoom7 = view.getResolutionForZoom(7);
    const resZoom8 = view.getResolutionForZoom(8);
    let scale = dfltScale;
    if (res > resZoom7) {
      const dynamicScale = resZoom7 / res;
      scale = dynamicScale < minIconScale ? minIconScale : dynamicScale;
    } else if (res < resZoom8) {
      scale = resZoom8 / res;
    }
    return parseFloat(scale.toFixed(1));
  },
  initialStation: 8000261,
  lineIcons,
  mapLineIcons,
  maskInfo: false,
  notificationTenant: "s-bahn-munchen",

  // Render functions
  renderAffectedRouteTitle: (affectedRoute) => {
    let start = "";
    let end = "";
    if (affectedRoute?.properties?.stops?.length) {
      const { stops } = affectedRoute.properties;
      start = stops[0]?.name || "";
      end = stops[stops.length - 1]?.name || "";
      // München Ost" must display "Ostbahnhof"
      start = start.replace(/münchen ost/i, "Ostbahnhof");
      end = end.replace(/münchen ost/i, "Ostbahnhof");
      // Remove münchen prefix, see SBAHNMW-660.
      start = start.replace(/münchen( |-)/i, "");
      end = end.replace(/münchen( |-)/i, "");
    }
    return `${start} ↔ ${end}`;
  },
  /**
   * Render functions components
   */
  renderStickyRouteHeader: ({ vehicle } = {}) => {
    if (vehicle) {
      // A trajectory can display only one event
      const event = eventsTrains.find((conf) => {
        return isEventTrain(vehicle, conf);
      });
      if (event?.header) {
        return event.header;
      }
    }
    return null;
  },
  schematic: {
    ...common.schematic,
    dataExtent: [
      -343587.64423760027, -275777.19694664655, 5505850.071994037,
      3635429.354084775,
    ],
    events: eventsSchematic,
    extent: schematicExtent,
    getZoomFromPreviousMode: (zoom) => {
      if (zoom - 5 >= 8) {
        return 5;
      }
      return zoom - 5;
    },
    maxZoom: 8.5,
    minZoom: 4,
    paintLineWidthHighlight: {
      stops: [
        [5, 4],
        [6, 8],
        [10, 40],
        // [6, 4],
        // [10.5, 8],
        // [11.5, 12],
      ],
    },
    style: styleSchematic,
    styleUrl: getStyleUrl(styleSchematic),
    x: 2406279,
    y: 1722649,
    z: 5.5,
  },
  sortVehicles: (trajectory, trajectory2) => {
    const vehicle1 = trajectory.properties;
    const vehicle2 = trajectory2.properties;

    // A trajectory can display only one event
    const event1 = eventsTrains.find((conf) => {
      return isEventTrain(vehicle1, conf);
    });
    if (event1) {
      return -1;
    }

    // Firefox doesn't react the same when we test only the first trajectory.
    // So we have to test both trajectories.
    const event2 = eventsTrains.find((conf) => {
      return isEventTrain(vehicle2, conf);
    });
    if (event2) {
      return 1;
    }

    return 0;
  },

  // Style functions
  standingVehicleStyleFunc,

  stationIcons: {
    ...common.stationIcons,
  },

  stationIconsLegend: true,

  stationIconsLegendPopup: true,

  title: "Live Map der S-Bahn München",
  topographic: {
    ...common.topographic,
    dataExtent: [1179506, 6055574, 1368764, 6200498],
    events: eventsTopographic,
    extent: topographicExtent,
    getZoomFromPreviousMode: (zoom) => {
      return zoom + 2;
    },
    maxZoom: 15,
    minZoom: 10,
    x: 1288648.23,
    y: 6129702.78,
    z: 10,
  },
  /**
   * Notifications functionnalities.
   */
  useNotifications: true,
};

// Custom permalink parameter
if (params.onlymap === "true") {
  config.bottomContainer = false;
  config.mapControls = false;
  config.legalLinks = false;
}

// videowall mode (SBAHNMW-718)
if (params.videowall === "true") {
  config.xyzParam = false;
  config.mapControls = false;
  config.maskInfo = false;
  config.platformInfo = false;
  config.routeInfo = false;
  config.departuresList = false;
  config.disruptionInfoCloseButton = false;
  config.mapUnderBottomContainer = true; // The map will not be resized when the bottom container is open (only desktop)
  config.navigatorClass = "videowall";
  config.allNotificationsOpen = true;
  config.notificationControls = false;
}

export default config;
